// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';

export default function Dashboard({ staff, onNavigate }) {
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showTaskPopup, setShowTaskPopup] = useState(false);
  const [assignedTasks, setAssignedTasks] = useState([]);
  
  const [roomTasks, setRoomTasks] = useState([
    { id: 1, room: '101', status: 'vacant', lastUpdated: '10:30 AM', assignedTo: 'Samar', priority: 'normal' },
    { id: 2, room: '102', status: 'cleaning', lastUpdated: '11:00 AM', assignedTo: 'Sara', priority: 'high' },
    { id: 3, room: '103', status: 'occupied', lastUpdated: '09:15 AM', assignedTo: '-', priority: 'normal' },
    { id: 4, room: '104', status: 'inspection', lastUpdated: '11:30 AM', assignedTo: 'Mohan', priority: 'normal' },
    { id: 5, room: '105', status: 'maintenance', lastUpdated: '08:45 AM', assignedTo: 'Mahindar', priority: 'urgent' },
  ]);

  // Staff login notification - Auto show task assignment popup
  useEffect(() => {
    if (staff && staff.name) {
      // Get tasks assigned to this staff member
      const myTasks = roomTasks.filter(task => task.assignedTo === staff.name);
      
      if (myTasks.length > 0) {
        setAssignedTasks(myTasks);
        setShowTaskPopup(true);
        
        // Auto hide after 10 seconds
        const timer = setTimeout(() => {
          setShowTaskPopup(false);
        }, 10000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [staff]);

  // Task timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      vacant: { label: 'Vacant', class: 'bg-green-100 text-green-700', icon: '✅' },
      cleaning: { label: 'Cleaning', class: 'bg-yellow-100 text-yellow-700', icon: '🧹' },
      occupied: { label: 'Occupied', class: 'bg-blue-100 text-blue-700', icon: '👤' },
      inspection: { label: 'Inspection', class: 'bg-purple-100 text-purple-700', icon: '🔍' },
      maintenance: { label: 'Maintenance', class: 'bg-red-100 text-red-700', icon: '🔧' },
    };
    return badges[status] || { label: status, class: 'bg-gray-100 text-gray-700', icon: '📋' };
  };

  const stats = {
    totalRooms: 42,
    todayTasks: 12,
    completed: 4,
    vacant: 5,
    maintenance: 2,
    occupancy: 78,
  };

  const handleStartTask = (roomNumber) => {
    setIsTimerRunning(true);
    setShowTaskPopup(false);
    // Navigate to room checklist or show success message
    alert(`Starting task for Room ${roomNumber}. Good luck! 🧹`);
  };

  const handleDismissTask = () => {
    setShowTaskPopup(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Task Assignment Popup Notification */}
      {showTaskPopup && assignedTasks.length > 0 && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in">
          <div className="bg-white rounded-xl shadow-2xl border-l-4 border-blue-600 max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  <h3 className="text-white font-semibold">New Task Assignment</h3>
                </div>
                <button onClick={handleDismissTask} className="text-white hover:text-gray-200">
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-2xl">
                  👤
                </div>
                <div>
                  <p className="text-sm text-gray-600">Welcome back,</p>
                  <p className="font-semibold text-gray-900">{staff?.name}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  You have been assigned {assignedTasks.length} task(s) for today:
                </p>
                <div className="space-y-2">
                  {assignedTasks.map(task => (
                    <div key={task.id} className="flex items-center justify-between bg-white rounded-lg p-2">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-blue-600">Room {task.room}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                          task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {task.priority.toUpperCase()} Priority
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Status: {getStatusBadge(task.status).label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleStartTask(assignedTasks[0]?.room)}
                  className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition font-medium"
                >
                  Start Task Now
                </button>
                <button
                  onClick={handleDismissTask}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition"
                >
                  Dismiss
                </button>
              </div>
              
              <p className="text-xs text-gray-400 text-center mt-3">
                This notification will auto-dismiss in 10 seconds
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Welcome back, {staff?.name || 'Staff'} • {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        
        {/* Task Timer Card */}
       
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Today's Tasks</p>
              <p className="text-2xl font-bold text-gray-900">{stats.todayTasks}</p>
              <p className="text-xs text-gray-400 mt-1">Pending: {stats.todayTasks - stats.completed} | Completed: {stats.completed}</p>
            </div>
            <div className="bg-blue-50 rounded-full p-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Vacant Rooms</p>
              <p className="text-2xl font-bold text-gray-900">{stats.vacant}</p>
              <p className="text-xs text-gray-400 mt-1">Ready for occupancy</p>
            </div>
            <div className="bg-green-50 rounded-full p-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Maintenance</p>
              <p className="text-2xl font-bold text-gray-900">{stats.maintenance}</p>
              <p className="text-xs text-gray-400 mt-1">Flagged issues</p>
            </div>
            <div className="bg-red-50 rounded-full p-3">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Occupancy Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats.occupancy}%</p>
              <p className="text-xs text-gray-400 mt-1">{stats.totalRooms - stats.vacant} / {stats.totalRooms} rooms</p>
            </div>
            <div className="bg-purple-50 rounded-full p-3">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Room Status Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Room Status Overview</h3>
            <p className="text-sm text-gray-500">Real-time room allocation and cleaning status</p>
          </div>
          <button 
            onClick={() => onNavigate('allocate')} 
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Allocate New Room
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {roomTasks.map((task) => {
                const statusBadge = getStatusBadge(task.status);
                const isAssignedToMe = task.assignedTo === staff?.name;
                return (
                  <tr key={task.id} className={`hover:bg-gray-50 transition ${isAssignedToMe ? 'bg-blue-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">
                        Room {task.room}
                        {isAssignedToMe && (
                          <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Assigned to you</span>
                        )}
                      </div>
                      {task.priority === 'urgent' && (
                        <span className="inline-block mt-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">Urgent</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusBadge.class}`}>
                        <span>{statusBadge.icon}</span>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {task.assignedTo !== '-' ? task.assignedTo : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{task.lastUpdated}</td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => setSelectedRoom(task.room)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium transition"
                      >
                        Checklist →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* My Assigned Tasks Section */}
      {assignedTasks.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">📋</span>
            <h3 className="text-lg font-semibold text-gray-900">Your Assigned Tasks</h3>
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">{assignedTasks.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {assignedTasks.map(task => (
              <div key={task.id} className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-gray-900">Room {task.room}</p>
                    <p className="text-xs text-gray-500">Status: {getStatusBadge(task.status).label}</p>
                  </div>
                  {task.priority === 'urgent' && (
                    <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full animate-pulse">URGENT</span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsTimerRunning(true);
                    alert(`Starting cleaning for Room ${task.room}. Good luck! 🧹`);
                  }}
                  className="w-full mt-2 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                >
                  Start Cleaning →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions & Updates */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 lg:col-span-1">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Quick Actions</h3>
          </div>
          <div className="p-4 space-y-2">
            <button 
              onClick={() => onNavigate('allocate')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition"
            >
              <span className="text-xl">🏨</span>
              <span>Allocate Rooms</span>
            </button>
            <button 
              onClick={() => onNavigate('tasks')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition"
            >
              <span className="text-xl">📋</span>
              <span>View All Tasks</span>
            </button>
            <button 
              onClick={() => onNavigate('audit')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition"
            >
              <span className="text-xl">📊</span>
              <span>Audit Reports</span>
            </button>
            <button 
              onClick={() => onNavigate('staff')}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition"
            >
              <span className="text-xl">👥</span>
              <span>Staff Management</span>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 lg:col-span-2">
          <div className="px-5 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Recent Activity</h3>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 mt-2 rounded-full bg-green-500"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Room 101 marked as <span className="font-medium">Vacant</span></p>
                <p className="text-xs text-gray-400">10:30 AM • Completed by John</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 mt-2 rounded-full bg-yellow-500"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Room 102 cleaning <span className="font-medium">in progress</span></p>
                <p className="text-xs text-gray-400">11:00 AM • Assigned to Sarah</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 mt-2 rounded-full bg-red-500"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Maintenance requested for <span className="font-medium">Room 105</span></p>
                <p className="text-xs text-gray-400">08:45 AM • Urgent priority</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 mt-2 rounded-full bg-blue-500"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">New room allocation for <span className="font-medium">Room 106</span></p>
                <p className="text-xs text-gray-400">09:20 AM • Assigned to Mike</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Room Details Modal */}
      {selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedRoom(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Room {selectedRoom} Details</h3>
              <button onClick={() => setSelectedRoom(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-600">Cleaning in Progress</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Assigned Staff:</span>
                <span className="font-medium">Sarah Johnson</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Start Time:</span>
                <span className="font-medium">11:00 AM</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Expected Completion:</span>
                <span className="font-medium">12:30 PM</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">View Checklist</button>
              <button onClick={() => setSelectedRoom(null)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom CSS for animation */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}